import React, { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Star, Loader2, CheckCircle2, ArrowRight, Mail, XCircle, ImagePlus, X as XIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const MAX_IMAGES = 5;

const StarPicker = ({ value, onChange }) => (
  <div className="flex items-center gap-1" data-testid="review-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className="p-1 transition-transform hover:scale-110"
        data-testid={`review-star-${n}`}
        aria-label={`${n} estrellas`}
      >
        <Star
          className={`w-9 h-9 ${n <= value ? 'fill-[#D47A4A] text-[#D47A4A]' : 'text-[#E7E5E4]'}`}
        />
      </button>
    ))}
  </div>
);

const ReviewSubmit = () => {
  const { orderNumber } = useParams();
  const [params] = useSearchParams();
  const [email, setEmail] = useState(params.get('email') || '');
  const [emailConfirmed, setEmailConfirmed] = useState(!!params.get('email'));
  const [eligibility, setEligibility] = useState(null);
  const [loadingEligibility, setLoadingEligibility] = useState(false);
  const [error, setError] = useState('');
  const [rating, setRating] = useState(0);
  const [title, setTitle] = useState('');
  const [comment, setComment] = useState('');
  const [images, setImages] = useState([]);       // [{url, name}]
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!emailConfirmed || !email) return;
    (async () => {
      setLoadingEligibility(true);
      setError('');
      try {
        const { data } = await axios.get(`${API}/tracking/review/eligibility`, {
          params: { order_number: orderNumber, email },
        });
        setEligibility(data);
      } catch (e) {
        setError(e.response?.data?.detail || 'No pudimos validar tu pedido con esos datos.');
        setEligibility(null);
      } finally {
        setLoadingEligibility(false);
      }
    })();
  }, [orderNumber, email, emailConfirmed]);

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) {
      setError('Por favor elige una calificación con las estrellas');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${API}/tracking/review`, {
        order_number: orderNumber,
        email,
        rating,
        title: title.trim() || null,
        comment: comment.trim() || null,
        images: images.map((i) => i.url),
      });
      setDone(true);
    } catch (e) {
      setError(e.response?.data?.detail || 'No pudimos guardar tu reseña.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFilesSelected = async (fileList) => {
    if (!fileList || !fileList.length) return;
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) {
      setError(`Máximo ${MAX_IMAGES} fotos por reseña`);
      return;
    }
    const files = Array.from(fileList).slice(0, remaining);
    setUploading(true);
    setError('');
    try {
      for (const f of files) {
        if (!/^image\/(jpe?g|png|webp)$/i.test(f.type)) {
          setError(`${f.name}: formato no soportado (usa JPG, PNG o WEBP)`);
          continue;
        }
        if (f.size > 8 * 1024 * 1024) {
          setError(`${f.name}: máximo 8 MB por imagen`);
          continue;
        }
        const fd = new FormData();
        fd.append('file', f);
        const { data } = await axios.post(
          `${API}/tracking/review/upload-image?order_number=${encodeURIComponent(orderNumber)}&email=${encodeURIComponent(email)}`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        setImages((prev) => [...prev, { url: data.url, name: f.name }]);
      }
    } catch (e) {
      setError(e.response?.data?.detail || 'No pudimos subir la imagen.');
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (url) => {
    setImages((prev) => prev.filter((i) => i.url !== url));
  };

  return (
    <div className="max-w-2xl mx-auto px-4 py-12">
      <div className="mb-6">
        <p className="text-xs tracking-[0.25em] uppercase text-[#D47A4A] font-semibold mb-2">Reseña</p>
        <h1 className="font-display font-bold text-3xl text-[#1C1917] tracking-tight" data-testid="review-title">
          Cuéntanos tu experiencia
        </h1>
        <p className="text-[#57534E] mt-2 text-sm">Pedido {orderNumber}</p>
      </div>

      {/* Ask email if not provided */}
      {!emailConfirmed && (
        <form onSubmit={(e) => { e.preventDefault(); setEmailConfirmed(true); }} className="bg-white rounded-3xl border border-[#E7E5E4] p-6 space-y-4" data-testid="review-email-form">
          <p className="text-sm text-[#57534E]">Ingresa el correo con el que hiciste el pedido para verificar.</p>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Correo</Label>
            <div className="relative mt-1">
              <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#8BA58F]" />
              <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                className="pl-10" data-testid="review-email-input" />
            </div>
          </div>
          <Button type="submit" className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full px-6" data-testid="review-email-submit">
            Continuar <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </form>
      )}

      {emailConfirmed && loadingEligibility && (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#2A5C43]" /></div>
      )}

      {emailConfirmed && !loadingEligibility && error && !eligibility && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 text-center" data-testid="review-eligibility-error">
          <XCircle className="w-10 h-10 text-red-600 mx-auto mb-3" />
          <p className="text-red-800 font-medium mb-3">{error}</p>
          <Button onClick={() => setEmailConfirmed(false)} variant="outline" className="rounded-full border-red-300 text-red-700">
            Probar con otro correo
          </Button>
        </div>
      )}

      {eligibility?.already_reviewed && !done && (
        <div className="bg-white rounded-3xl border border-[#E7E5E4] p-8 text-center" data-testid="review-already">
          <CheckCircle2 className="w-12 h-12 text-[#2A5C43] mx-auto mb-3" />
          <h2 className="font-display font-bold text-xl text-[#1C1917] mb-2">Ya nos dejaste tu opinión</h2>
          <p className="text-[#57534E] text-sm mb-4">Gracias por tu reseña 🐾</p>
          {eligibility.existing_review && (
            <div className="bg-[#F7F2E8] rounded-2xl p-4 text-left max-w-md mx-auto">
              <div className="flex items-center gap-1 mb-2">
                {[1,2,3,4,5].map(n => (
                  <Star key={n} className={`w-5 h-5 ${n <= eligibility.existing_review.rating ? 'fill-[#D47A4A] text-[#D47A4A]' : 'text-[#E7E5E4]'}`} />
                ))}
              </div>
              {eligibility.existing_review.title && <p className="font-semibold text-[#1C1917]">{eligibility.existing_review.title}</p>}
              {eligibility.existing_review.comment && <p className="text-sm text-[#57534E] mt-1">{eligibility.existing_review.comment}</p>}
            </div>
          )}
          <Button asChild className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full mt-6">
            <Link to="/perros">Volver al catálogo</Link>
          </Button>
        </div>
      )}

      {eligibility?.can_review && !eligibility.already_reviewed && !done && (
        <form onSubmit={submit} className="bg-white rounded-3xl border border-[#E7E5E4] p-6 sm:p-8 space-y-5" data-testid="review-form">
          <div className="text-center">
            <p className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold mb-3">¿Cómo calificas tu experiencia?</p>
            <div className="flex justify-center"><StarPicker value={rating} onChange={setRating} /></div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Título (opcional)</Label>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Resume tu opinión en pocas palabras" data-testid="review-title-input" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">Cuéntanos más (opcional)</Label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              maxLength={2000}
              rows={5}
              placeholder="¿Cómo le fue a tu mascota con este alimento? ¿Cómo fue el proceso de compra y entrega?"
              className="w-full mt-1 px-4 py-3 rounded-xl border border-[#E7E5E4] text-sm focus:outline-none focus:border-[#2A5C43] resize-none"
              data-testid="review-comment"
            />
            <p className="text-xs text-[#8BA58F] mt-1 text-right">{comment.length}/2000</p>
          </div>

          {/* Image uploads */}
          <div data-testid="review-images-section">
            <Label className="text-xs uppercase tracking-widest text-[#8BA58F] font-semibold">
              Fotos (opcional) — máx {MAX_IMAGES}
            </Label>
            <div className="mt-2 grid grid-cols-3 sm:grid-cols-5 gap-3">
              {images.map((img) => (
                <div key={img.url} className="relative aspect-square rounded-xl overflow-hidden border border-[#E7E5E4] group">
                  <img src={`${BACKEND}${img.url}?size=400`} alt={img.name} className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeImage(img.url)}
                    className="absolute top-1 right-1 bg-black/60 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                    aria-label="Quitar imagen"
                    data-testid={`review-remove-image`}
                  >
                    <XIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))}
              {images.length < MAX_IMAGES && (
                <label
                  className={`aspect-square rounded-xl border-2 border-dashed border-[#E7E5E4] hover:border-[#2A5C43] flex flex-col items-center justify-center gap-1 cursor-pointer text-[#8BA58F] hover:text-[#2A5C43] transition-colors ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
                  data-testid="review-image-add"
                >
                  {uploading ? (
                    <Loader2 className="w-6 h-6 animate-spin" />
                  ) : (
                    <>
                      <ImagePlus className="w-6 h-6" />
                      <span className="text-[10px] font-semibold uppercase tracking-widest">Añadir</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    className="sr-only"
                    onChange={(e) => handleFilesSelected(e.target.files)}
                    disabled={uploading}
                  />
                </label>
              )}
            </div>
            <p className="text-xs text-[#8BA58F] mt-2">
              Muestra a tu mascota disfrutando o el empaque del producto. JPG, PNG o WEBP · máx 8 MB c/u.
            </p>
          </div>

          {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg p-3" data-testid="review-error">{error}</p>}
          <Button type="submit" disabled={submitting || !rating}
            className="w-full bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full py-5 font-semibold" data-testid="review-submit">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            Enviar mi reseña
          </Button>
          <p className="text-xs text-[#8BA58F] text-center">Al enviar tu reseña aceptas que sea publicada de forma anónima en el sitio.</p>
        </form>
      )}

      {done && (
        <div className="bg-gradient-to-br from-[#F7F2E8] to-[#FFF7EE] rounded-3xl border border-[#E7E5E4] p-10 text-center" data-testid="review-done">
          <CheckCircle2 className="w-14 h-14 text-[#2A5C43] mx-auto mb-4" />
          <h2 className="font-display font-bold text-2xl text-[#1C1917] mb-2">¡Gracias por tu reseña!</h2>
          <p className="text-[#57534E] text-sm mb-6">Nos ayuda muchísimo. Tu opinión ya está publicada 🐾</p>
          <Button asChild className="bg-[#2A5C43] hover:bg-[#214A35] text-white rounded-full">
            <Link to="/perros">Explorar el catálogo <ArrowRight className="w-4 h-4 ml-2" /></Link>
          </Button>
        </div>
      )}
    </div>
  );
};

export default ReviewSubmit;
